import { VoiceEffect, type ScriptBlock, type ScriptDocument } from '@estudio/contracts';

/** Nodo ProseMirror/Tiptap mínimo. */
export interface PmNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PmNode[];
  text?: string;
  marks?: unknown[];
}

const BLOCK_ID_RE = /^[A-Za-z0-9_-]{4,64}$/;

export function newBlockId(random: () => string = defaultRandom): string {
  return `b_${random()}`;
}

function defaultRandom(): string {
  const bytes = new Uint8Array(8);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function nodeText(node: PmNode): string {
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  const parts = (node.content ?? []).map(nodeText);
  const sep = node.type === 'bulletList' || node.type === 'orderedList' || node.type === 'listItem' ? '\n' : '';
  return parts.join(sep);
}

/**
 * Convierte el JSON del editor a documento por bloques. Cada bloque de primer
 * nivel conserva su blockId; si falta o está duplicado (p. ej. tras pegar), se
 * genera uno nuevo sin tocar los demás.
 */
export function editorToScript(doc: PmNode, random?: () => string): ScriptDocument {
  const seen = new Set<string>();
  const blocks: ScriptBlock[] = (doc.content ?? []).map((node) => {
    let id = typeof node.attrs?.blockId === 'string' ? (node.attrs.blockId as string) : '';
    if (!BLOCK_ID_RE.test(id) || seen.has(id)) id = newBlockId(random);
    seen.add(id);
    const { blockId: _drop, voiceEffect: rawEffect, ...restAttrs } = node.attrs ?? {};
    const voice_effect = rawEffect == null ? undefined : VoiceEffect.parse(rawEffect);
    const cleanNode: PmNode = { ...node, attrs: Object.keys(restAttrs).length ? restAttrs : undefined };
    if (cleanNode.attrs === undefined) delete cleanNode.attrs;
    return { id, type: node.type, text: nodeText(node), ...(voice_effect === undefined ? {} : { voice_effect }), node: cleanNode as unknown as Record<string, unknown> };
  });
  return { schema_version: 1, type: 'doc', blocks };
}

export function scriptToEditor(script: ScriptDocument): PmNode {
  return {
    type: 'doc',
    content: script.blocks.map((b) => {
      const node = (b.node as unknown as PmNode | undefined) ?? {
        type: 'paragraph',
        content: b.text ? [{ type: 'text', text: b.text }] : [],
      };
      const { voiceEffect: storedEffect, ...attrs } = node.attrs ?? {};
      // Valida también metadata antigua: un valor nuevo no se descarta en silencio.
      const previous = storedEffect == null ? undefined : VoiceEffect.parse(storedEffect);
      const effect = b.voice_effect === undefined ? previous : VoiceEffect.parse(b.voice_effect);
      return { ...node, attrs: { ...attrs, blockId: b.id, ...(effect === undefined ? {} : { voiceEffect: effect }) } };
    }),
  };
}

export function emptyScript(): ScriptDocument {
  return { schema_version: 1, type: 'doc', blocks: [] };
}

/** Contenido canónico para comparar sin depender del orden de claves. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

export type BlockDiffKind = 'unchanged' | 'changed' | 'added' | 'removed';
export interface BlockDiff {
  kind: BlockDiffKind;
  id: string;
  left?: ScriptBlock;
  right?: ScriptBlock;
}

/**
 * Compara dos revisiones por id de bloque (LCS sobre ids) para mostrar qué
 * cambió sin perder la correspondencia de comentarios anclados.
 */
export function diffScripts(left: ScriptDocument, right: ScriptDocument): BlockDiff[] {
  const a = left.blocks;
  const b = right.blocks;
  const n = a.length;
  const m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i]!.id === b[j]!.id ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: BlockDiff[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    const x = a[i]!;
    const y = b[j]!;
    if (x.id === y.id) {
      out.push({ kind: x.text === y.text && x.voice_effect === y.voice_effect && canonicalJson(x.node) === canonicalJson(y.node) ? 'unchanged' : 'changed', id: x.id, left: x, right: y });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ kind: 'removed', id: x.id, left: x });
      i++;
    } else {
      out.push({ kind: 'added', id: y.id, right: y });
      j++;
    }
  }
  while (i < n) out.push({ kind: 'removed', id: a[i]!.id, left: a[i++]! });
  while (j < m) out.push({ kind: 'added', id: b[j]!.id, right: b[j++]! });
  return out;
}

export function wordCount(script: ScriptDocument): number {
  return script.blocks.reduce((acc, b) => acc + (b.text.trim() ? b.text.trim().split(/\s+/).length : 0), 0);
}

/**
 * Estimación de duración: indica siempre su base. Sin voz medida, se usa el
 * ritmo configurado del perfil y se etiqueta como tal.
 */
export function estimateDuration(script: ScriptDocument, pace: { wordsPerMinute: number; basis: 'configured' | 'measured' }) {
  const words = wordCount(script);
  const seconds = pace.wordsPerMinute > 0 ? Math.round((words / pace.wordsPerMinute) * 60) : 0;
  return { words, seconds, basis: pace.basis };
}
